import { GetResourceUtilizationResponse, ResourceUtilizationAllocation } from '../../api-service/clients';
import { formatDate } from '../../utilities';

export type ResourceUtilizationView = 'employee' | 'unit' | 'offer' | 'grantType';

export const resourceUtilizationViews: Array<{ view: ResourceUtilizationView; label: string }> = [
  { view: 'employee', label: 'Medarbejder' },
  { view: 'unit', label: 'Udførerenhed' },
  { view: 'offer', label: 'Tilbud' },
  { view: 'grantType', label: 'Bevillingstype' },
];

export interface ResourceUtilizationFilters {
  serviceProviderUnitId?: string;
  offerId?: string;
  grantTypeId?: string;
}

export type ResourceUtilizationStatus = { kind: 'over' | 'full' | 'free'; text: string };

export interface ResourceUtilizationActivity {
  activityId: string;
  activityIdentifier: number;
  activityTitle?: string;
  grantTypeName: string;
  offerName: string;
  startDate: string;
  allocated: string;
}

export interface ResourceUtilizationRow {
  key: string;
  name: string;
  description: string;
  activityCount: number;
  allocated: string;
  otherAllocated?: string;
  capacity: string;
  bar: { allocatedPercent: number; otherPercent: number; overPercent: number; freePercent: number };
  percentage: string;
  status?: ResourceUtilizationStatus;
  canDrillDown: boolean;
  activities: ResourceUtilizationActivity[];
  activitiesTotal: string;
}

export interface ResourceUtilizationKpis {
  capacity: string;
  employeeCount: number;
  allocatedLabel: string;
  allocated: string;
  allocatedPercentage: string;
  activityCount: number;
  free: string;
  freeEmployeeCount: number;
  over: string;
  overEmployeeCount: number;
}

export interface ResourceUtilizationOverview {
  kpis: ResourceUtilizationKpis;
  rows: ResourceUtilizationRow[];
  total: { activityCount: number; allocated: string; capacity: string };
  showsCapacity: boolean;
}

export interface ResourceUtilizationChip {
  filter: keyof ResourceUtilizationFilters;
  label: string;
}

const NO_GRANT_TYPE_KEY = 'no-grant-type';

interface Group {
  key: string;
  activityIds: Set<string>;
  allocatedMinutes: number;
  otherMinutes: number;
  capacityMinutes: number;
  offerIds: Set<string>;
}

export function formatHours(minutes: number): string {
  const rounded = Math.round((minutes / 60) * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace('.', ',');
  return `${text} t`;
}

export function formatHoursAndMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) {
    return `${hours} t`;
  }
  return hours ? `${hours} t ${rest} min` : `${rest} min`;
}

function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)} %`;
}

export function grantTypeLabel(data: GetResourceUtilizationResponse, grantTypeId: string | undefined, short = false): string {
  const grantType = data.grantTypes.find(x => x.id === grantTypeId);
  if (!grantType) {
    return 'Uden bevillingstype';
  }
  return short ? grantType.shortName : grantType.name;
}

export function offersForUnit(data: GetResourceUtilizationResponse, serviceProviderUnitId: string | undefined) {
  return data.offers.filter(offer => !serviceProviderUnitId || offer.serviceProviderUnitId === serviceProviderUnitId);
}

export function selectServiceProviderUnit(
  data: GetResourceUtilizationResponse,
  filters: ResourceUtilizationFilters,
  serviceProviderUnitId: string | undefined
): ResourceUtilizationFilters {
  const selectedOffer = data.offers.find(offer => offer.id === filters.offerId);
  const keepsOffer = !!serviceProviderUnitId && selectedOffer?.serviceProviderUnitId === serviceProviderUnitId;
  return { ...filters, serviceProviderUnitId, offerId: keepsOffer ? filters.offerId : undefined };
}

export function selectOffer(data: GetResourceUtilizationResponse, filters: ResourceUtilizationFilters, offerId: string | undefined): ResourceUtilizationFilters {
  const offer = data.offers.find(x => x.id === offerId);
  return { ...filters, offerId, serviceProviderUnitId: offer ? offer.serviceProviderUnitId : filters.serviceProviderUnitId };
}

export function removeFilter(filters: ResourceUtilizationFilters, filter: keyof ResourceUtilizationFilters): ResourceUtilizationFilters {
  if (filter === 'serviceProviderUnitId') {
    return { ...filters, serviceProviderUnitId: undefined, offerId: undefined };
  }
  return { ...filters, [filter]: undefined };
}

export function filterChips(data: GetResourceUtilizationResponse, filters: ResourceUtilizationFilters): ResourceUtilizationChip[] {
  const chips: ResourceUtilizationChip[] = [];
  const unit = data.serviceProviderUnits.find(x => x.id === filters.serviceProviderUnitId);
  if (unit) {
    chips.push({ filter: 'serviceProviderUnitId', label: `Udførerenhed: ${unit.name}` });
  }
  const offer = data.offers.find(x => x.id === filters.offerId);
  if (offer) {
    chips.push({ filter: 'offerId', label: `Tilbud: ${offer.name}` });
  }
  if (filters.grantTypeId) {
    chips.push({ filter: 'grantTypeId', label: `Bevillingstype: ${grantTypeLabel(data, filters.grantTypeId)}` });
  }
  return chips;
}

function aggregate(
  data: GetResourceUtilizationResponse,
  filters: ResourceUtilizationFilters,
  keyOf: (employeeId: string, offerId: string, grantTypeId?: string) => string,
  withCapacity: boolean
): Group[] {
  const unitOfOffer = new Map(data.offers.map(offer => [offer.id, offer.serviceProviderUnitId]));
  const groups = new Map<string, Group>();

  function isOfferInFilter(offerId: string) {
    return (!filters.serviceProviderUnitId || unitOfOffer.get(offerId) === filters.serviceProviderUnitId) && (!filters.offerId || offerId === filters.offerId);
  }

  function group(key: string): Group {
    let existing = groups.get(key);
    if (!existing) {
      existing = { key, activityIds: new Set(), allocatedMinutes: 0, otherMinutes: 0, capacityMinutes: 0, offerIds: new Set() };
      groups.set(key, existing);
    }
    return existing;
  }

  if (withCapacity) {
    for (const capacity of data.capacities) {
      if (isOfferInFilter(capacity.offerId)) {
        const target = group(keyOf(capacity.organizationUserId, capacity.offerId));
        target.capacityMinutes += capacity.weeklyCapacityMinutes;
        target.offerIds.add(capacity.offerId);
      }
    }
  }

  for (const allocation of data.allocations) {
    if (!isOfferInFilter(allocation.offerId)) {
      continue;
    }
    const isOnSelectedGrantType = !filters.grantTypeId || allocation.grantTypeId === filters.grantTypeId;
    if (!withCapacity && !isOnSelectedGrantType) {
      continue;
    }
    const target = group(keyOf(allocation.organizationUserId, allocation.offerId, allocation.grantTypeId ?? NO_GRANT_TYPE_KEY));
    if (isOnSelectedGrantType) {
      target.activityIds.add(allocation.activityId);
      target.allocatedMinutes += allocation.allocatedMinutesPerWeek;
      target.offerIds.add(allocation.offerId);
    } else {
      target.otherMinutes += allocation.allocatedMinutesPerWeek;
    }
  }

  return [...groups.values()].filter(g => !filters.grantTypeId || g.allocatedMinutes > 0);
}

function keyFunction(view: ResourceUtilizationView, unitOfOffer: Map<string, string>): (employeeId: string, offerId: string, grantTypeId?: string) => string {
  return (employeeId, offerId, grantTypeId) => {
    switch (view) {
      case 'employee':
        return employeeId;
      case 'unit':
        return unitOfOffer.get(offerId) ?? '';
      case 'offer':
        return offerId;
      case 'grantType':
        return grantTypeId ?? NO_GRANT_TYPE_KEY;
    }
  };
}

function isInFilter(allocation: ResourceUtilizationAllocation, filters: ResourceUtilizationFilters, unitOfOffer: Map<string, string>) {
  return (
    (!filters.serviceProviderUnitId || unitOfOffer.get(allocation.offerId) === filters.serviceProviderUnitId) &&
    (!filters.offerId || allocation.offerId === filters.offerId) &&
    (!filters.grantTypeId || allocation.grantTypeId === filters.grantTypeId)
  );
}

function capacityStatus(capacityMinutes: number, usedMinutes: number): ResourceUtilizationStatus {
  const difference = capacityMinutes - usedMinutes;
  if (difference < 0) {
    return { kind: 'over', text: `${formatHours(-difference)} over` };
  }
  if (capacityMinutes && usedMinutes / capacityMinutes >= 0.9) {
    return { kind: 'full', text: 'Fuld' };
  }
  return { kind: 'free', text: `${formatHours(difference)} ledig` };
}

export function buildResourceUtilizationOverview(
  data: GetResourceUtilizationResponse,
  filters: ResourceUtilizationFilters,
  view: ResourceUtilizationView
): ResourceUtilizationOverview {
  const unitOfOffer = new Map(data.offers.map(offer => [offer.id, offer.serviceProviderUnitId]));
  const showsCapacity = view !== 'grantType';
  const groups = aggregate(data, filters, keyFunction(view, unitOfOffer), showsCapacity);

  const scale = Math.max(1, ...groups.map(g => Math.max(g.allocatedMinutes + g.otherMinutes, g.capacityMinutes)));
  const maxAllocated = Math.max(1, ...groups.map(g => g.allocatedMinutes));
  const totalAllocated = groups.reduce((sum, g) => sum + g.allocatedMinutes, 0);

  const rows = groups.map(g => {
    const usedMinutes = g.allocatedMinutes + g.otherMinutes;
    const activities = view === 'employee' ? employeeActivities(data, filters, g.key, unitOfOffer) : [];
    const row: ResourceUtilizationRow & { sortKey: number } = {
      key: g.key,
      name: rowName(data, view, g.key),
      description: rowDescription(data, view, g, filters),
      activityCount: g.activityIds.size,
      allocated: formatHours(g.allocatedMinutes),
      otherAllocated: g.otherMinutes > 0 ? formatHours(g.otherMinutes) : undefined,
      capacity: showsCapacity ? formatHours(g.capacityMinutes) : '–',
      bar: showsCapacity
        ? {
            allocatedPercent: (Math.min(g.allocatedMinutes, g.capacityMinutes) / scale) * 100,
            otherPercent: (Math.min(g.otherMinutes, Math.max(0, g.capacityMinutes - g.allocatedMinutes)) / scale) * 100,
            overPercent: (Math.max(0, usedMinutes - g.capacityMinutes) / scale) * 100,
            freePercent: (Math.max(0, g.capacityMinutes - usedMinutes) / scale) * 100,
          }
        : { allocatedPercent: (g.allocatedMinutes / maxAllocated) * 100, otherPercent: 0, overPercent: 0, freePercent: 0 },
      percentage: showsCapacity
        ? g.capacityMinutes
          ? formatPercent(usedMinutes / g.capacityMinutes)
          : '–'
        : formatPercent(totalAllocated ? g.allocatedMinutes / totalAllocated : 0),
      status: showsCapacity ? capacityStatus(g.capacityMinutes, usedMinutes) : undefined,
      canDrillDown: view !== 'employee' && !(view === 'grantType' && g.key === NO_GRANT_TYPE_KEY),
      activities: activities.map(a => ({
        activityId: a.activityId,
        activityIdentifier: a.activityIdentifier,
        activityTitle: a.activityTitle,
        grantTypeName: grantTypeLabel(data, a.grantTypeId, true),
        offerName: data.offers.find(o => o.id === a.offerId)?.name ?? '',
        startDate: formatDate(a.startDate),
        allocated: formatHoursAndMinutes(a.allocatedMinutesPerWeek),
      })),
      activitiesTotal: formatHoursAndMinutes(activities.reduce((sum, a) => sum + a.allocatedMinutesPerWeek, 0)),
      sortKey: showsCapacity ? (g.capacityMinutes ? usedMinutes / g.capacityMinutes : 0) : g.allocatedMinutes,
    };
    return row;
  });
  rows.sort((a, b) => b.sortKey - a.sortKey || a.name.localeCompare(b.name, 'da'));

  const kpis = buildKpis(data, filters);
  return {
    kpis,
    rows: rows.map(({ sortKey, ...row }) => row),
    total: {
      activityCount: kpis.activityCount,
      allocated: kpis.allocated,
      capacity: showsCapacity ? kpis.capacity : '–',
    },
    showsCapacity,
  };
}

function employeeActivities(data: GetResourceUtilizationResponse, filters: ResourceUtilizationFilters, employeeId: string, unitOfOffer: Map<string, string>) {
  return data.allocations
    .filter(a => a.organizationUserId === employeeId && isInFilter(a, filters, unitOfOffer))
    .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
}

function rowName(data: GetResourceUtilizationResponse, view: ResourceUtilizationView, key: string): string {
  switch (view) {
    case 'employee':
      return data.employees.find(e => e.organizationUserId === key)?.name ?? 'Ukendt medarbejder';
    case 'unit':
      return data.serviceProviderUnits.find(u => u.id === key)?.name ?? 'Ukendt udførerenhed';
    case 'offer':
      return data.offers.find(o => o.id === key)?.name ?? 'Ukendt tilbud';
    case 'grantType':
      return grantTypeLabel(data, key);
  }
}

function rowDescription(data: GetResourceUtilizationResponse, view: ResourceUtilizationView, group: Group, filters: ResourceUtilizationFilters): string {
  const offers = data.offers.filter(o => group.offerIds.has(o.id));
  const unitName = (unitId: string) => data.serviceProviderUnits.find(u => u.id === unitId)?.name ?? '';
  switch (view) {
    case 'employee':
      return [...new Set(offers.map(o => unitName(o.serviceProviderUnitId)))].filter(Boolean).join(', ');
    case 'unit': {
      const count = data.offers.filter(o => o.serviceProviderUnitId === group.key && (!filters.offerId || o.id === filters.offerId)).length;
      return `${count} tilbud`;
    }
    case 'offer':
      return unitName(data.offers.find(o => o.id === group.key)?.serviceProviderUnitId ?? '');
    case 'grantType':
      return offers.map(o => o.name).join(', ');
  }
}

export function buildKpis(data: GetResourceUtilizationResponse, filters: ResourceUtilizationFilters): ResourceUtilizationKpis {
  const perEmployee = aggregate(data, filters, employeeId => employeeId, true);
  let capacity = 0;
  let allocated = 0;
  let free = 0;
  let over = 0;
  let freeEmployeeCount = 0;
  let overEmployeeCount = 0;
  const activityIds = new Set<string>();

  for (const employee of perEmployee) {
    capacity += employee.capacityMinutes;
    allocated += employee.allocatedMinutes;
    employee.activityIds.forEach(id => activityIds.add(id));
    const difference = employee.capacityMinutes - employee.allocatedMinutes - employee.otherMinutes;
    if (difference > 0) {
      free += difference;
      freeEmployeeCount++;
    } else if (difference < 0) {
      over -= difference;
      overEmployeeCount++;
    }
  }

  return {
    capacity: formatHours(capacity),
    employeeCount: perEmployee.length,
    allocatedLabel: filters.grantTypeId ? `Allokeret på ${grantTypeLabel(data, filters.grantTypeId, true)}` : 'Allokeret pr. uge',
    allocated: formatHours(allocated),
    allocatedPercentage: capacity ? formatPercent(allocated / capacity) : '–',
    activityCount: activityIds.size,
    free: formatHours(free),
    freeEmployeeCount,
    over: formatHours(over),
    overEmployeeCount,
  };
}

export function drillDown(
  data: GetResourceUtilizationResponse,
  filters: ResourceUtilizationFilters,
  view: ResourceUtilizationView,
  key: string
): { filters: ResourceUtilizationFilters; view: ResourceUtilizationView } {
  switch (view) {
    case 'unit':
      return { filters: { ...filters, serviceProviderUnitId: key, offerId: undefined }, view: 'offer' };
    case 'offer':
      return { filters: selectOffer(data, filters, key), view: 'employee' };
    case 'grantType':
      return { filters: { ...filters, grantTypeId: key }, view: 'employee' };
    case 'employee':
      return { filters, view };
  }
}
